import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import Messages from './Messages';
import Footer from './Footer';
import FormDropdown from '../TestDataForm/FormDropdown';

const SplitView = ({ 
  messages, 
  streamingMessage, 
  messagesEndRef, 
  handleSendMessage, 
  isProcessing,
  onShowForm,
  websocket,
  chatWidth = "100%",
  onSplitChange,
  isActiveForm = false,
  style = {}
}) => {
  const [currentWidth, setCurrentWidth] = useState(chatWidth);
  const [isDragging, setIsDragging] = useState(false);
  const [disabledForms, setDisabledForms] = useState({
    prepaidDisabled: false,
    postpaidDisabled: false
  });

  // Derive formState from props and state - isOpen is controlled by parent
  const formState = {
    isOpen: isActiveForm,
    prepaidDisabled: disabledForms.prepaidDisabled,
    postpaidDisabled: disabledForms.postpaidDisabled
  };

  // Handle mouse events for resize
  const handleMouseDown = useCallback((e) => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      
      const container = document.querySelector('[data-testid="split-container"]');
      if (!container) return;
      
      const containerRect = container.getBoundingClientRect();
      const position = ((e.clientX - containerRect.left) / containerRect.width) * 100;
      
      // Get screen width
      const screenWidth = window.innerWidth;
      
      // Adjust limits based on screen size
      const minPosition = screenWidth < 850 ? 30 : 20;
      const maxPosition = screenWidth < 850 ? 40 : 60;
      
      // Limit the split position
      const limitedPosition = Math.min(Math.max(position, minPosition), maxPosition);
      onSplitChange(limitedPosition);
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    setIsDragging(true);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [isDragging, onSplitChange]);



  const handleFormState = useCallback((event) => {
    const { type, detail } = event;
    setDisabledForms(prev => ({
      ...prev,
      [`${type}Disabled`]: detail
    }));
  }, []);

  useEffect(() => {
    ['postpaid', 'prepaid'].forEach(type => {
      window.addEventListener(`${type}FormState`, handleFormState);
    });

    return () => {
      ['postpaid', 'prepaid'].forEach(type => {
        window.removeEventListener(`${type}FormState`, handleFormState);
      });
    };
  }, [handleFormState]);

  const handleFormDisplay = useCallback((formType) => {
    if (formType) {
      if (formState.isOpen) {
        // If a form is already open, close it first then open the new one
        setCurrentWidth(chatWidth);
        onShowForm?.(null);

        setTimeout(() => {
          setCurrentWidth(chatWidth.includes("%") ? chatWidth : "40%");
          onShowForm?.(formType);
        }, 300);
      } else {
        setCurrentWidth(chatWidth.includes("%") ? chatWidth : "40%");
        onShowForm?.(formType);
      }
    } else {
      setCurrentWidth(chatWidth);
      onShowForm?.(null);
    }
  }, [chatWidth, formState.isOpen, onShowForm]);

  const enableForms = useCallback(() => {
    setCurrentWidth(chatWidth);
    setDisabledForms({
      prepaidDisabled: false,
      postpaidDisabled: false
    });
    onShowForm?.(null);
  }, [chatWidth, onShowForm]);

  useEffect(() => {
    window.enableFormButtons = enableForms;
    return () => {
      delete window.enableFormButtons;
    };
  }, [enableForms]);
  
  // Update currentWidth when chatWidth changes or when active form state changes
  useEffect(() => {
    if (isActiveForm) {
      setCurrentWidth(chatWidth);
    } else {
      // Reset any resize-specific state when in standalone mode
      setCurrentWidth("100%");
    }
  }, [chatWidth, isActiveForm]);

  return (
    <Box
      sx={{
        height: "100%",
        width: isActiveForm ? currentWidth : "100%",
        maxWidth: isActiveForm ? currentWidth : "850px",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        ...style
      }}
    >

      <Box
        p={3}
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          bgcolor: "#FFF",
          borderRadius: "4px"
        }}
      >
        <Messages 
          messages={messages.filter(msg => msg.type !== 'form')} 
          websocket={websocket} 
          existingWebSocket={websocket} 
        />
        {streamingMessage.message && <Messages messages={[streamingMessage]} />}
        <div ref={messagesEndRef} />
      </Box>

      <Box sx={{ p: 2 }}>
        <Box sx={{ mb: 0.5, display: 'flex', justifyContent: 'center' }}>
          <FormDropdown
            onFormSelect={handleFormDisplay}
            formStates={formState}
            isProcessing={isProcessing}
          />
        </Box>

        <Footer
          onSendMessage={handleSendMessage}
          disable={isProcessing}
        />
      </Box>
    </Box>
  );
};

SplitView.propTypes = {
  messages: PropTypes.array.isRequired,
  streamingMessage: PropTypes.object.isRequired,
  messagesEndRef: PropTypes.object.isRequired,
  handleSendMessage: PropTypes.func.isRequired,
  isProcessing: PropTypes.bool.isRequired,
  onShowForm: PropTypes.func,
  websocket: PropTypes.object,
  chatWidth: PropTypes.string,
  onSplitChange: PropTypes.func,
  isActiveForm: PropTypes.bool,
  style: PropTypes.object
};

export default SplitView;
